import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/data/queryKeys';
import { useAuth } from '@/contexts/AuthContext';
import { meldingFeedbackService } from '../services/meldingFeedbackService';
import type { Feedback } from '../lib/feedbackRegels';

/** Mijn beoordeelde meldingen die nog op feedback wachten (Mijn Inbox › Taken). */
export function useFeedbackGevraagd(enabled = true) {
  const { user } = useAuth();
  return useQuery({
    queryKey: queryKeys.meldingen.feedbackGevraagd(user?.id),
    queryFn: () => meldingFeedbackService.feedbackGevraagd(user!.id),
    enabled: enabled && !!user,
    staleTime: 60 * 1000,
  });
}

/**
 * Feedback geven, met optionele foto's (max 5; eerst uploaden, dan de RPC).
 * Ververst daarna alle meldingenlijsten: de feedbackvragen, "Mijn meldingen"
 * in de meldknop en de softwarelijst van Tom.
 */
export function useFeedbackGeven() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      meldingId: string;
      feedback: Feedback;
      opmerking: string;
      fotos?: File[];
    }) => {
      const paden =
        input.fotos && input.fotos.length > 0 && user
          ? await meldingFeedbackService.uploadFotos(user.id, input.fotos)
          : [];
      await meldingFeedbackService.geefFeedback(
        input.meldingId,
        input.feedback,
        input.opmerking,
        paden,
      );
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: queryKeys.meldingen.alles }),
        qc.invalidateQueries({ queryKey: ['software_meldingen'] }),
      ]);
    },
  });
}

/** Signed links naar de feedbackfoto's van een melding (badge voor Tom/meekijkers). */
export function useFeedbackFotoLinks(paden: string[] | null | undefined) {
  const lijst = paden ?? [];
  return useQuery({
    queryKey: queryKeys.meldingen.feedbackFotos(lijst),
    queryFn: () => meldingFeedbackService.fotoLinks(lijst),
    enabled: lijst.length > 0,
    staleTime: 5 * 60 * 1000,
  });
}
